import { Matrix4, Matrix4Tuple, Object3D, Quaternion, Vector3 } from 'three';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { CoordinateManager } from '../../coordinate-manager';
import { Coordinates, coordinateToPoint3d } from '../../utilities/math-helpers';
import { Glb } from '../glb';

export interface FileVersionTransformation {
  fileVersionId: string;
  transform?: Matrix4Tuple;
}

export function getActiveFileVersionTransformation(
  asset: Glb,
  offset: Vector3,
): {
  position: Vector3;
  quaternion: Quaternion;
  scale: Vector3;
} {
  /**
   * The matrix4 tuple
   */
  const m = asset.fileVersionTransformations.find(
    (x) => x.fileVersionId === asset.selectedVersion?.id,
  )?.transform;
  const position = new Vector3(0, 0, 0);
  const quaternion = new Quaternion();
  const scale = new Vector3(1, 1, 1);

  if (isDefined(m)) {
    const matrix = new Matrix4();

    /**
     * Note: the matrix is being set in an inversed order
     * (By columns instead of rows)
     *
     * i.e like this:
     * 1 3
     * 2 4
     *
     * not this:
     * 1 2
     * 3 4
     */
    // prettier-ignore
    matrix.set(
      m[0], m[4], m[8],  m[12],
      m[1], m[5], m[9],  m[13],
      m[2], m[6], m[10], m[14],
      m[3], m[7], m[11], m[15],
    );

    matrix.decompose(position, quaternion, scale);
  }

  // If the position is zero then we use the position from the asset for backwards compatibility
  // With old texture transformations which doesn't contain the position.
  if (position.length() === 0) {
    position.add(offset);
  } else {
    const localPos = CoordinateManager.getInstance().projectToLocal(position.x, position.y);
    position.set(localPos.x, localPos.y, position.z - CoordinateManager.getInstance().getMAS());
  }

  return { position, quaternion, scale };
}

export function applyFileVersionTransformation(
  object: Object3D,
  q: Quaternion,
  scale: Vector3,
  position = new Vector3(0, 0, 0),
) {
  object.setRotationFromQuaternion(q);
  object.scale.set(scale.x, scale.y, scale.z);
  object.updateMatrix();
  object.position.set(position.x, position.y, position.z);
  object.matrixWorldNeedsUpdate = true;
}

export async function saveCurrentTransformation(asset: Glb) {
  if (!isDefined(asset.selectedVersion)) {
    return;
  }

  const transform = getMatrix4Tuple(
    asset,
    new Quaternion(),
    new Vector3(1, 1, 1),
    CoordinateManager.getInstance().localToProjectCoords(asset.position.clone()),
  );
  await saveFileVersionTransformation(asset, transform);
}

/**
 * Copies the transformation matrix of the base object and returns a modified matrix, rotated, scaled and positioned by the given values.
 */
export function getMatrix4Tuple(
  baseObject: Object3D,
  q: Quaternion,
  scale: Vector3,
  coordinates: Coordinates,
) {
  const position = coordinateToPoint3d(coordinates);
  const object = new Object3D();
  object.applyMatrix4(baseObject.matrix.clone());

  object.applyQuaternion(q);
  object.scale.multiply(scale);
  object.updateMatrix();
  object.position.set(position.x, position.y, position.z);
  object.matrixWorldNeedsUpdate = true;

  const matrix4 = object.matrix.clone();
  matrix4.setPosition(object.position.clone());

  const transform = matrix4.toArray();

  return transform;
}

export async function saveFileVersionTransformation(asset: Glb, transform: Matrix4Tuple) {
  const fileVersionId = asset.selectedVersion?.id;
  const dynamicAssetId = asset.assetId;

  if (!isDefined(dynamicAssetId)) {
    throw new Error('Error saving file version transformation. Dynamic asset ID is undefined.');
  }

  if (!isDefined(fileVersionId)) {
    throw new Error('Error saving file version transformation. No file version selected.');
  }

  const existingTransform = asset.fileVersionTransformations.find(
    (x) => x.fileVersionId === fileVersionId,
  );

  if (existingTransform) {
    existingTransform.transform = transform;
  } else {
    asset.fileVersionTransformations.push({ fileVersionId, transform });
  }

  await SkyMapAxiosServiceFactory.instance.createDynamicAssetServiceV0().putFileVersionTransform({
    path: { dynamicAssetId, fileVersionId },
    body: { transform },
  });
}
